// UseAskAiSocketEvents.ts
import { getZaniaSocket, safeEmit } from "@/infra/sockets";
import { addNotification } from "@/shared/states/notification";
import dayjs from "dayjs";
import { useCallback, useEffect, useRef, useState } from "react";
import { Socket } from "socket.io-client";
import { useChatStore } from "../states";
import {
  AiMessageDeltaPayload, AiMessageEndPayload, AiMessageStartPayload,
  ClientToServerEvents, ErrorMessagePayload, ServerToClientEvents, ThreadCreatedPayload
} from "../types";

let isListenerRegistered = false; // module-level flag

export default function UseAskAiSocketEvents() {
  const socket = getZaniaSocket() as Socket<ServerToClientEvents, ClientToServerEvents>;
  const { threadId, setThreadId, isGenerating, setIsGenerating, appendToMessage, uploadedFiles, addAllSourceToMessage, setUploadedFiles, setIsOpen, addMessage, setSelectedSession } = useChatStore();
  
  const channelRef = useRef<BroadcastChannel | null>(null);
  // Track the active thread ID for message filtering
  const activeThreadIdRef = useRef<string>(threadId);
  // Track which thread is currently generating content
  const generatingThreadIdRef = useRef<string | null>(null);
  
  // Update the ref when threadId changes
  useEffect(() => {
    // If threadId changes and it's different from the currently generating thread
    // we should reset the isGenerating state
    if (threadId !== activeThreadIdRef.current) {
      if (isGenerating && generatingThreadIdRef.current !== threadId) {
        setIsGenerating(false);
      }
      activeThreadIdRef.current = threadId;
    }
  }, [threadId, isGenerating]);

  // Also reset generating state when component unmounts for cleanup
  useEffect(() => {
    return () => {
      if (generatingThreadIdRef.current) {
        generatingThreadIdRef.current = null;
      }
    };
  }, []);

  // Handle broadcast channel setup
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'user')
        addMessage({ role: "user", message: event.data.message as string, created_at: dayjs().toString() });
      if (event.data.type === "assistant") {
        addMessage({ role: "assistant", message: "", created_at: dayjs().toString() });
        setUploadedFiles([]);
      }
    };
    
    try {
      channelRef.current = new BroadcastChannel('ask-ai-broadcast');

      if (!isListenerRegistered) {
        channelRef.current.addEventListener("message", handleMessage);
      }

      return () => {
        if (isListenerRegistered && channelRef.current) {
          channelRef.current.removeEventListener("message", handleMessage);
          channelRef.current.close();
        }
      };
    } catch (error) {
      console.warn("BroadcastChannel not supported in this browser", error);
    }
  }, []);

  useEffect(() => {
    // Only register if not already registered
    if (!isListenerRegistered) {
      const handleThreadCreated = (
        data:
          | ThreadCreatedPayload
          | AiMessageStartPayload
          | AiMessageDeltaPayload
          | AiMessageEndPayload
          | ErrorMessagePayload
      ) => {
        // Filter messages by thread ID to prevent incorrect binding
        if (data.type !== 'thread_created' && 
            'thread_id' in data && 
            data.thread_id !== activeThreadIdRef.current) {
          return;
        }

        switch (data.type) {
          case "thread_created":
            setThreadId(data.thread_created.thread_id);
            activeThreadIdRef.current = data.thread_created.thread_id;
            // Clear the generating thread reference when creating a new thread
            generatingThreadIdRef.current = null;
            setIsGenerating(false);
            break;
          case "ai_message_start":
            // Set the generating thread ID when generation starts
            generatingThreadIdRef.current = data.thread_id;
            setIsGenerating(true);
            setIsOpen(true);
            appendToMessage(data.ai_message_start.message);
            break;
          case "ai_message_delta": {
            let temp = data.ai_message_delta.message;
            temp = temp.replace(/([.,!?]+)(\s*)?(\[[^\]]*\])/g, "$3$2$1");

            appendToMessage(temp);
            if (data.ai_message_delta.inline_sources?.length) {
              try {
                appendToMessage(JSON.stringify(data.ai_message_delta.inline_sources));
              } catch (error) {
                console.error("Failed to parse inline sources:", error);
              }
            }
            break;
          }
          case "ai_message_end":
            if (data.ai_message_end.all_sources) {
              addAllSourceToMessage(data.ai_message_end.all_sources);
            }
            setTimeout(() => {
              const conversation = document.querySelector("#conversation");
              conversation?.scrollTo({
                top: conversation.scrollHeight,
                behavior: "smooth"
              });
            }, 100);
            // Clear the generating thread reference when generation ends
            if (generatingThreadIdRef.current === data.thread_id) {
              generatingThreadIdRef.current = null;
              setIsGenerating(false);
            }
            break;
          case "error_message":
            if (data.error_message.error_module === 'SESSION_SELECTION') {
              setSelectedSession({});
              addNotification({ 
                title: 'Invalid Session', 
                message: `Assessment results are not available for the selected session, which may affect responses to your query. Scope has been reset to "All Sessions"`, 
                type: "error" 
              });
              return;
            }
            addMessage({ role: "assistant", message: data.error_message.message, type: "error", created_at: dayjs().toString() });
            setTimeout(() => {
              const conversation = document.querySelector("#conversation");
              conversation?.scrollTo({
                top: conversation.scrollHeight,
                behavior: "smooth"
              });
            }, 100);
            // Clear the generating thread reference on error
            if (generatingThreadIdRef.current === data.thread_id) {
              generatingThreadIdRef.current = null;
              setIsGenerating(false);
            }
            break;
          default:
            console.log("Unhandled event:", data);
        }
      };

      socket.on("ask_ai", handleThreadCreated);
      isListenerRegistered = true;

      return () => {
        socket.off("ask_ai", handleThreadCreated);
        isListenerRegistered = false;
      };
    }
  }, [socket, setThreadId, setIsGenerating, appendToMessage, setIsOpen, addMessage, setSelectedSession]);

  // Utility function to ensure thread exists before performing actions
  const ensureThreadExists = useCallback((): Promise<string> => {
    return new Promise((resolve, reject) => {
      // If thread already exists, return it
      if (threadId) {
        resolve(threadId);
        return;
      }

      // Otherwise create a new thread
      safeEmit("ask_ai", { type: "new_thread", new_thread: null }, (resp: any) => {
        if (resp?.error) {
          console.error("Error creating new thread:", resp.error);
          reject(resp.error);
          return;
        }
        
        // The thread ID will be set by the listener, we just need to wait for it
        const checkThreadInterval = setInterval(() => {
          if (activeThreadIdRef.current) {
            clearInterval(checkThreadInterval);
            resolve(activeThreadIdRef.current);
          }
        }, 100);
      });
    });
  }, [threadId, socket]);

  // Helper functions to emit events
  const createNewThread = useCallback((): Promise<string> => {
    // Make sure to clear any existing generation state
    if (generatingThreadIdRef.current) {
      // Attempt to cancel the previous generation first
      safeEmit("ask_ai", {
        type: "cancel_generation",
        thread_id: generatingThreadIdRef.current,
      });
      
      generatingThreadIdRef.current = null;
      setIsGenerating(false);
    }
    
    return new Promise((resolve, reject) => {
      safeEmit("ask_ai", { type: "new_thread", new_thread: null }, (resp: any) => {
        if (resp?.error) {
          console.error("Error creating new thread:", resp.error);
          reject(resp.error);
          return;
        }
        setIsOpen(false);
        
        // Wait for the thread ID to be set
        const checkThreadInterval = setInterval(() => {
          if (activeThreadIdRef.current) {
            clearInterval(checkThreadInterval);
            resolve(activeThreadIdRef.current);
          }
        }, 100);
      });
    });
  }, [socket, setIsGenerating, setIsOpen]);

  const uploadUserFile = useCallback((tId: string, fileUrls: string[]): void => {
    safeEmit("ask_ai", {
      type: "user_file_upload",
      thread_id: tId,
      user_file_upload: { file_urls: fileUrls },
    });
  }, [socket]);

  const sendSelectedSessions = useCallback(async (tId: string | null, sessionIds: "all" | string[]): Promise<void> => {
    try {
      // Ensure thread exists if not provided
      const threadToUse = tId || await ensureThreadExists();
      
      safeEmit("ask_ai", {
        type: "selected_sessions",
        thread_id: threadToUse,
        selected_sessions: { session_ids: sessionIds },
      }, (resp: any) => {
        if (resp?.error) {
          console.error("Error selecting session:", resp.error);
          setSelectedSession({});
          return;
        }
      });
    } catch (error) {
      console.error("Failed to send selected sessions:", error);
      setSelectedSession({});
    }
  }, [socket, ensureThreadExists, setSelectedSession]);

  const sendUserMessage = useCallback(async (tId: string | null, message: string): Promise<void> => {
    try {
      // Ensure thread exists if not provided
      const threadToUse = tId || await ensureThreadExists();
      
      // Only continue if we have a valid thread ID
      if (!threadToUse) {
        console.error("Cannot send message without a thread ID");
        return;
      }
      
      setIsOpen(true);
      // Track which thread is generating content
      generatingThreadIdRef.current = threadToUse;
      setIsGenerating(true);
      
      let appendMsg = message;
      if (uploadedFiles?.length) {
        appendMsg += `   
        Attachments: ${uploadedFiles.map((file) => file?.file?.name).join(', ')}`
      }
      
      try {
        channelRef.current?.postMessage({ type: 'user', message: appendMsg });
      } catch (error) {
        console.warn("Failed to broadcast user message", error);
        // Continue anyway - add message directly if broadcasting fails
        addMessage({ role: "user", message: appendMsg, created_at: dayjs().toString() });
      }
      
      safeEmit("ask_ai", {
        type: "user_message",
        thread_id: threadToUse,
        user_message: { message }
      }, (resp: any) => {
        if (resp?.error) {
          console.error("Error sending message", resp.error);
          // Clear generating state on error
          if (generatingThreadIdRef.current === threadToUse) {
            generatingThreadIdRef.current = null;
            setIsGenerating(false);
          }
          return;
        }
      });
      
      try {
        channelRef.current?.postMessage({ type: 'assistant' });
      } catch (error) {
        console.warn("Failed to broadcast assistant message", error);
        // Continue anyway - add empty assistant message directly if broadcasting fails
        addMessage({ role: "assistant", message: "", created_at: dayjs().toString() });
      }
    } catch (error) {
      console.error("Failed to send user message:", error);
      setIsGenerating(false);
    }
  }, [socket, uploadedFiles, setIsOpen, setIsGenerating, addMessage, ensureThreadExists]);

  // Enhanced cancel generation function
  const cancelGeneration = useCallback((): void => {
    const threadToCancel = generatingThreadIdRef.current || activeThreadIdRef.current;
    
    if (!threadToCancel) return;
    
    // Clear our local tracking first
    if (generatingThreadIdRef.current) {
      generatingThreadIdRef.current = null;
    }
    
    setIsGenerating(false);
  }, [socket, setIsGenerating]);

  return {
    socket,
    createNewThread,
    uploadUserFile,
    sendSelectedSessions,
    sendUserMessage,
    cancelGeneration,
    ensureThreadExists
  };
}
